import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, MenuItem, Select, FormControl, alpha,
} from "@mui/material";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import {
  useCreatePlaylistMutation,
  useGetCompanyQuery,
  useGetPlaylistGroupsQuery,
} from "../../Api/attoDeskApi";
import { IPlaylist, IPlaylistGroup, MenuType } from "../../Api/Interface/api.interface";
import { MENU_TYPE_LABELS, MENU_TYPE_HELP } from "../../Pages/Playlist/Edit/menuScheduleShared";
import { useAppSelector } from "../../Store/hooks";
import { selectIsSuperAdmin, selectCurrentUser } from "../../Store/Auth/AuthSelector";
import { useNotifier } from "../../Core/Notifier";
import { NAVY } from "../../theme/tokens";
import PlaylistGroupSelect from "../ui/PlaylistGroupSelect";

/**
 * Shared "New Playlist" creation modal.
 *
 * This is the single source of truth for lightweight playlist creation — the
 * same name + company + validation + API used by the Playlists list. Callers
 * decide what happens after a playlist is created via `onCreated` (e.g. the
 * list navigates into the editor, the group picker adds it to the group).
 * Keeping the form here means the creation logic is not duplicated.
 */
const CreatePlaylistDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onCreated: (playlist: IPlaylist) => void;
  /** Optional pre-selected company (super admin only). */
  defaultCompanyId?: number | "";
  /**
   * Optional pre-selected playlist group. Passed by the Playlist Group
   * workflow so a playlist created from inside a group is assigned to it
   * on creation (via playlist_group_id). null / "" => "— No Playlist
   * Group —".
   */
  defaultPlaylistGroupId?: number | "";
  /**
   * When true the group selector is locked to defaultPlaylistGroupId — used
   * by the Playlist Group workflow where the target group is fixed.
   */
  lockPlaylistGroup?: boolean;
}> = ({
  open, onClose, onCreated, defaultCompanyId = "",
  defaultPlaylistGroupId = "", lockPlaylistGroup = false,
}) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);
  const currentUser = useAppSelector(selectCurrentUser);
  const [createPlaylist, { isLoading: isCreating }] = useCreatePlaylistMutation();

  const { data: companiesData } = useGetCompanyQuery(
    { searchText: "", id: "" } as any,
    { skip: !isSuperAdmin }
  );
  const companies: Array<{ id: number; companyName: string }> = Array.isArray(
    (companiesData as any)?.data
  )
    ? ((companiesData as any).data as Array<{ id: number; companyName: string }>)
    : [];

  const { data: groupsData } = useGetPlaylistGroupsQuery(undefined, { skip: !open });
  const allGroups: IPlaylistGroup[] = Array.isArray((groupsData as any)?.data)
    ? ((groupsData as any).data as IPlaylistGroup[])
    : [];

  const [name, setName] = useState("");
  const [menuType, setMenuType] = useState<MenuType>("standard_playlist");
  const [companyId, setCompanyId] = useState<number | "">(defaultCompanyId);
  const [groupId, setGroupId] = useState<number | "">(defaultPlaylistGroupId);

  // Reset the form each time the dialog is opened.
  useEffect(() => {
    if (open) {
      setName("");
      setMenuType("standard_playlist");
      setCompanyId(defaultCompanyId);
      setGroupId(defaultPlaylistGroupId);
    }
  }, [open, defaultCompanyId, defaultPlaylistGroupId]);

  const MENU_TYPE_OPTIONS: MenuType[] = [
    "standard_playlist", "default_menu", "daily_special", "time_based_menu",
    "holiday_menu", "promotional_campaign", "emergency_override",
  ];

  // The company the new playlist (and any selected group) belongs to:
  // the picked company for super_admin, the caller's own company otherwise.
  const effectiveCompanyId: number | "" = isSuperAdmin
    ? companyId
    : (currentUser?.company_id ?? "");

  // Only the effective company's active groups (+ the locked default) are
  // offered. Changing the company resets a group that no longer fits.
  const companyGroups = useMemo(() => {
    return allGroups.filter(
      (g) =>
        g.is_active &&
        (effectiveCompanyId === "" || g.company_id == null || g.company_id === effectiveCompanyId)
    );
  }, [allGroups, effectiveCompanyId]);

  const handleCompanyChange = (next: number | "") => {
    setCompanyId(next);
    if (lockPlaylistGroup) return;
    if (groupId !== "") {
      const stillValid = allGroups.some(
        (g) => g.id === groupId && (next === "" || g.company_id == null || g.company_id === next)
      );
      if (!stillValid) setGroupId("");
    }
  };

  const handleCreate = async () => {
    if (isCreating) return;
    if (!name.trim()) {
      showErrorMessage("Name is required");
      return;
    }
    if (isSuperAdmin && companyId === "") {
      showErrorMessage("Please pick a company");
      return;
    }
    try {
      const payload: any = { name: name.trim(), is_active: true, menu_type: menuType };
      if (isSuperAdmin) payload.company_id = Number(companyId);
      // Assign to the selected group on creation (null => no group).
      payload.playlist_group_id = groupId === "" ? null : Number(groupId);
      const res = await createPlaylist(payload).unwrap();
      if (res.status) {
        const created = (res as any).data as IPlaylist;
        showMessage("Playlist created");
        onCreated(created);
      } else {
        showErrorMessage((res as any).message || "Failed to create playlist");
      }
    } catch (err: any) {
      showErrorMessage(err?.data?.message || "Failed to create playlist");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => { if (!isCreating) onClose(); }}
      maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: NAVY, pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{
            width: 32, height: 32, borderRadius: 2,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: `linear-gradient(135deg, ${alpha("#7c3aed", 0.12)} 0%, ${alpha("#7c3aed", 0.06)} 100%)`,
          }}>
            <PlaylistPlayIcon sx={{ fontSize: 16, color: "#7c3aed" }} />
          </Box>
          New Playlist
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.75, textTransform: "uppercase", letterSpacing: "0.02em" }}>
          What are you creating?
        </Typography>
        <FormControl fullWidth size="small" sx={{ mb: 0.75 }}>
          <Select
            value={menuType}
            onChange={(e) => setMenuType(e.target.value as MenuType)}
            sx={{
              fontSize: 13, borderRadius: 2.5,
              "& fieldset": { borderColor: "#e2e8f0" },
              "&.Mui-focused fieldset": { borderColor: "#7c3aed", borderWidth: 2 },
            }}
          >
            {MENU_TYPE_OPTIONS.map((t) => (
              <MenuItem key={t} value={t}>{MENU_TYPE_LABELS[t]}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography sx={{ fontSize: 11.5, color: "#64748b", mb: 2 }}>
          {MENU_TYPE_HELP[menuType]}
          {menuType !== "standard_playlist" && menuType !== "default_menu"
            ? " Set the days/times on the Schedule tab after creating."
            : ""}
        </Typography>

        {isSuperAdmin && (
          <>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.75, textTransform: "uppercase", letterSpacing: "0.02em" }}>
              Company *
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <Select
                value={companyId === "" ? "" : String(companyId)}
                onChange={(e) =>
                  handleCompanyChange(e.target.value === "" ? "" : Number(e.target.value))
                }
                displayEmpty
                sx={{
                  fontSize: 13, borderRadius: 2.5,
                  "& fieldset": { borderColor: "#e2e8f0" },
                  "&.Mui-focused fieldset": { borderColor: "#7c3aed", borderWidth: 2 },
                }}
              >
                <MenuItem value="">— Pick a company —</MenuItem>
                {companies.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>{c.companyName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        {/* Playlist Group — directly below Company, above Playlist Name. */}
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.75, textTransform: "uppercase", letterSpacing: "0.02em" }}>
          Playlist Group
        </Typography>
        <Box sx={{ mb: 2 }}>
          <PlaylistGroupSelect
            value={groupId}
            onChange={setGroupId}
            groups={companyGroups}
            disabled={lockPlaylistGroup}
            createInCompanyId={effectiveCompanyId === "" ? null : effectiveCompanyId}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2.5, fontSize: 13, bgcolor: "#fff",
                "& fieldset": { borderColor: "#e2e8f0" },
                "&.Mui-focused fieldset": { borderColor: "#7c3aed", borderWidth: 2 },
              },
            }}
          />
        </Box>

        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#475569", mb: 0.75, textTransform: "uppercase", letterSpacing: "0.02em" }}>
          Playlist Name *
        </Typography>
        <TextField
          autoFocus fullWidth size="small"
          placeholder="e.g. Morning Loop"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2.5, fontSize: 13, bgcolor: "#fff",
              "& fieldset": { borderColor: "#e2e8f0" },
              "&.Mui-focused fieldset": { borderColor: "#7c3aed", borderWidth: 2 },
            },
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          size="small" onClick={onClose} disabled={isCreating}
          sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, color: "#64748b" }}
        >
          Cancel
        </Button>
        <Button
          size="small" variant="contained" disableElevation
          disabled={!name.trim() || isCreating}
          onClick={handleCreate}
          sx={{
            borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 700,
            background: "linear-gradient(135deg, #7c3aed 0%, #8b5cf6 100%)",
            "&:hover": { background: "linear-gradient(135deg, #6d28d9 0%, #7c3aed 100%)" },
          }}
        >
          {isCreating ? "Creating..." : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreatePlaylistDialog;
